/**
 * Backend configuration for Procore vs Novorender infrastructure
 */

export type BackendProvider = "novorender" | "procore";

export interface BackendConfig {
    provider: BackendProvider;
    authServerUrl: string;
    dataV2ServerUrl: string;
    projectsUrl: string;
    assetsUrl: string;
}

/**
 * Environment-specific configuration for Procore assets.
 *
 * NOTE: Using Novorender's assets URL temporarily until Procore assets CDN is ready.
 * These are public static files (skyboxes, environment maps) that don't require auth.
 */
const PROCORE_ASSETS_HOST = "https://assets.novorender.com"; // TODO: Change to Procore CDN when available

/**
 * Procore regional datacenter identifiers.
 * Used for routing to region-specific services and infrastructure.
 */
export const Region = {
    LOCAL: "local",
    STAGING: "staging",
    US01: "us01",
    US02: "us02",
    UK01: "uk01",
} as const;

/**
 * Type for regional datacenter identifiers
 */
export type Region = (typeof Region)[keyof typeof Region];

/**
 * Regional datacenter mapping based on hostname patterns.
 * Pattern inspired by payments-ui-service DOMAIN_MAP approach.
 * Uses Map<RegExp, Region> with single regex alternation for cleaner matching.
 */
const REGION_MAP = new Map<RegExp, Region>([
    // Local development
    [/localhost/i, Region.LOCAL],
    [/127\.0\.0\.1/i, Region.LOCAL],
    // UK region (eu-west-02)
    [/uk01\.procore\.com/i, Region.UK01],
    // US West region (na-west-02)
    [/(us02|sandbox-monthly-us02)\.procore\.com/i, Region.US02],
    // US East explicit regional subdomain
    [/us01\.procore\.com/i, Region.US01],
    // Staging/QA environments
    [/[^.]+\.procoretech-qa\.com/i, Region.STAGING],
    // US East region (na-east-01) - default production
    [/(app|sandbox|api-sandbox-monthly)\.procore\.com/i, Region.US01],
    [/app\.procoregov\.com/i, Region.US01],
]);

/**
 * Detect Procore regional datacenter based on hostname.
 *
 * Uses the payments-ui-service DOMAIN_MAP pattern for consistent
 * region detection across frontend applications.
 *
 * @example
 * ```ts
 * // With explicit hostname
 * detectRegion("app.procore.com") // → "us01"
 * detectRegion("us02.procore.com") // → "us02"
 * detectRegion("app.uk01.procore.com") // → "uk01"
 * detectRegion("staging1.procoretech-qa.com") // → "staging"
 * detectRegion("localhost") // → "local"
 *
 * // Auto-detect from current window
 * detectRegion() // uses window.location.hostname
 * ```
 *
 * @param hostname - Optional hostname to detect region for. Defaults to window.location.hostname
 * @returns The detected regional datacenter identifier
 */
export function detectRegion(hostname?: string): Region {
    // Get hostname from parameter or window.location
    const host = hostname ?? globalThis.window?.location?.hostname;

    // If no hostname available (SSR/Node without parameter), default to us01
    if (!host) {
        return Region.US01;
    }

    // Find matching region pattern
    for (const [pattern, region] of REGION_MAP) {
        if (pattern.test(host)) {
            return region;
        }
    }

    // Fallback to us01 for unrecognized domains
    return Region.US01;
}

export const StandaloneEnv = {
    LOCAL: "local",
    TEST: "test",
    PRODUCTION: "production",
} as const;

export type StandaloneEnv = (typeof StandaloneEnv)[keyof typeof StandaloneEnv];

export function detectStandaloneEnv(hostname?: string): StandaloneEnv {
    const host = hostname ?? globalThis.window?.location?.hostname;

    if (host === "explorer.novorender.com") {
        return StandaloneEnv.PRODUCTION;
    }

    if (host.endsWith("novorender.com") || host.endsWith("azurewebsites.net")) {
        return StandaloneEnv.TEST;
    }

    return StandaloneEnv.LOCAL;
}

/**
 * Get backend configuration based on provider
 */
export function getBackendConfig(provider: BackendProvider = "novorender"): BackendConfig {
    if (provider === "procore") {
        // Allow env var override for local dev/testing, otherwise auto-detect
        const envOverride = import.meta.env.REACT_APP_DATA_V2_SERVER_URL;
        const assetsOverride = import.meta.env.REACT_APP_ASSETS_URL;

        let apiHost: string;
        let assetsHost: string;

        if (envOverride) {
            // Explicit override via env var (for local dev with custom backend)
            apiHost = envOverride;
            assetsHost = assetsOverride ?? PROCORE_ASSETS_HOST;
        } else {
            // Auto-detect based on hostname
            // Since app.procore.com and api.procore.com resolve to the same infrastructure,
            // use the current origin for same-origin requests (avoids CORS preflight).
            // Use empty string for relative paths in SSR/Node environments.
            apiHost = globalThis.window?.location.origin ?? "";
            assetsHost = PROCORE_ASSETS_HOST;
        }

        return {
            provider: "procore",
            authServerUrl: `${apiHost}/oauth`,
            dataV2ServerUrl: apiHost,
            projectsUrl: apiHost,
            assetsUrl: assetsHost,
        };
    }

    // Default: Novorender
    return {
        provider: "novorender",
        authServerUrl: import.meta.env.REACT_APP_AUTH_SERVER_URL ?? "https://auth.novorender.com",
        dataV2ServerUrl: import.meta.env.REACT_APP_DATA_V2_SERVER_URL ?? "https://data-v2.novorender.com",
        projectsUrl: import.meta.env.REACT_APP_PROJECTS_URL ?? "https://projects.novorender.com",
        assetsUrl: import.meta.env.REACT_APP_ASSETS_URL ?? "https://assets.novorender.com",
    };
}
